import { useTranslation } from 'react-i18next';
import { SMBold, MDRegular } from '@/typography/Typography';
import {
    Box,
    Divider,
    Stack,
    Table,
    TableBody,
    TableContainer
} from '@mui/material';
import {
    TableCell,
    TableFooter,
    TableFooterCell,
    TableRow
} from '@/components/shared/styles/Table';
import CardHeader from '@/components/shared/CardHeader';
import { TableCard } from '@/components/shared/Card';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
    PrimaryButton,
    SecondaryButtonRegular
} from '@/components/shared/Button';
import { DATE_FORMAT, EXCEL_DATE_FORMAT } from '@/lib/constants';
import dayjs, { Dayjs } from 'dayjs';
import { useFormatting } from '@/utils/formatting';
import { UserContext } from '@/contexts/user/UserContext';
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from 'xlsx';
import { TipsReconciliationTable } from '@/pages/tips/TipsReconciliationTable';
import {
    useEstimatedTipsReconciliationReport,
    useActivePayrollPeriods,
    useCurrentCompanyDetails
} from '@/services/paymentsQuery';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import { SearchGrid } from '@/components/SearchGrid';
import { Dropdown } from '@/components/shared/Dropdown';

export const TipsActivePayrollPage = () => {
    const { t } = useTranslation();
    const [selectedPeriod, setSelectedPeriod] = useState<{
        start: string;
        end: string;
        label: string;
    } | null>(null);

    const {
        data: activePeriodsData,
        error: periodsError,
        isPending: periodsLoading
    } = useActivePayrollPeriods();

    const periods = useMemo(() => {
        return activePeriodsData?.data?.periods;
    }, [activePeriodsData?.data?.periods]);

    // Set the first period as selected by default when data loads
    useEffect(() => {
        if (periods && periods?.length > 0 && !selectedPeriod) {
            const timeoutId = setTimeout(() => {
                setSelectedPeriod(periods?.[0]);
            }, 200);
            return () => clearTimeout(timeoutId);
        }
    }, [periods, selectedPeriod]);

    // Calculate month and year from selected period's start date
    const selectedMonth = useMemo(() => {
        return selectedPeriod
            ? dayjs(selectedPeriod.end).format('MM')
            : dayjs().format('MM');
    }, [selectedPeriod]);

    const selectedYear = useMemo(() => {
        return selectedPeriod
            ? dayjs(selectedPeriod.end).format('YYYY')
            : dayjs().format('YYYY');
    }, [selectedPeriod]);

    const {
        data: tipsReconciliation,
        error,
        isPending
    } = useEstimatedTipsReconciliationReport(selectedMonth, selectedYear);

    useHandleResponse({
        error,
        isPending,
        error2: periodsError,
        isPending2: periodsLoading
    });

    const { formatDate, getBalanceLabel, formatDateTime } = useFormatting();

    const dateHeader = useMemo(() => {
        return selectedPeriod
            ? dayjs(selectedPeriod.start).format(DATE_FORMAT.MONTH_YEAR)
            : dayjs().format(DATE_FORMAT.MONTH_YEAR);
    }, [selectedPeriod]);

    const monthName = useMemo(() => {
        return selectedPeriod
            ? dayjs(selectedPeriod.start).format(DATE_FORMAT.MONTH_NAME)
            : dayjs().format(DATE_FORMAT.MONTH_NAME);
    }, [selectedPeriod]);

    const yearName = useMemo(() => {
        return selectedPeriod
            ? dayjs(selectedPeriod.start).format(DATE_FORMAT.YEAR)
            : dayjs().format(DATE_FORMAT.YEAR);
    }, [selectedPeriod]);

    const { company } = useContext(UserContext);

    // Helper function to format payroll period
    const formatPayrollPeriod = (start: string, end: string) => {
        return (
            formatDate(start, DATE_FORMAT.DAY_MONTH, true) +
            ' - ' +
            formatDate(end, DATE_FORMAT.DAY_MONTH, true)
        );
    };

    const onPressDownload = () => {
        const sheet = XLSXUtils.aoa_to_sheet([]);
        const sheetData: any[] = [[monthName, yearName], ['']];
        const boldCells: string[] = [];
        let currentRow = 3;

        const header = [
            t('employeeType'),
            t('date'),
            t('distributedAt'),
            t('reportedAt'),
            t('description'),
            t('amount'),
            t('sum')
        ];

        // Process hourly employees
        const hourlyReconciliations =
            tipsReconciliation?.data?.hourlyEmployeesTipsReconciliation;

        if (hourlyReconciliations) {
            hourlyReconciliations.forEach((period: any) => {
                const periodText = formatPayrollPeriod(
                    period.payrollPeriodStart,
                    period.payrollPeriodEnd
                );
                const periodEmployees: any[] = [];

                period.tipsTransactions?.forEach((transaction: any) => {
                    periodEmployees.push([
                        t('hourly_employees'),
                        formatDate(
                            transaction.date,
                            EXCEL_DATE_FORMAT.DATE,
                            true
                        ),
                        formatDate(
                            transaction.createdAt,
                            EXCEL_DATE_FORMAT.TIMESTAMP
                        ),
                        formatDate(
                            transaction.reportedAt,
                            EXCEL_DATE_FORMAT.TIMESTAMP
                        ),
                        transaction.description,
                        transaction.amount,
                        transaction.sum
                    ]);
                });

                sheetData.push([t('hourly_employees'), periodText]);
                currentRow++;

                sheetData.push(header);
                boldCells.push(
                    `A${currentRow}`,
                    `B${currentRow}`,
                    `C${currentRow}`,
                    `D${currentRow}`,
                    `E${currentRow}`,
                    `F${currentRow}`
                );
                currentRow++;

                sheetData.push(...periodEmployees);
                currentRow += periodEmployees.length;

                sheetData.push([t('total'), '', '', '', period.total, '']);
                boldCells.push(`A${currentRow}`, `E${currentRow}`);
                currentRow++;

                sheetData.push(['']);
                currentRow++;
            });
        }

        // Process fulltime employees
        const fulltimeReconciliations =
            tipsReconciliation?.data?.fullTimeEmployeesTipsReconciliation;

        if (fulltimeReconciliations) {
            fulltimeReconciliations.forEach((period: any) => {
                const periodText = formatPayrollPeriod(
                    period.payrollPeriodStart,
                    period.payrollPeriodEnd
                );
                const periodEmployees: any[] = [];

                period.tipsTransactions?.forEach((transaction: any) => {
                    periodEmployees.push([
                        t('fulltime_employees'),
                        formatDate(
                            transaction.date,
                            EXCEL_DATE_FORMAT.DATE,
                            true
                        ),
                        formatDate(
                            transaction.createdAt,
                            EXCEL_DATE_FORMAT.TIMESTAMP
                        ),
                        formatDate(
                            transaction.reportedAt,
                            EXCEL_DATE_FORMAT.TIMESTAMP
                        ),
                        transaction.description,
                        transaction.amount,
                        transaction.sum
                    ]);
                });

                sheetData.push([t('fulltime_employees'), periodText]);
                currentRow++;

                sheetData.push(header);
                boldCells.push(
                    `A${currentRow}`,
                    `B${currentRow}`,
                    `C${currentRow}`,
                    `D${currentRow}`,
                    `E${currentRow}`,
                    `F${currentRow}`
                );
                currentRow++;

                sheetData.push(...periodEmployees);
                currentRow += periodEmployees.length;

                sheetData.push([t('total'), '', '', '', period.total, '']);
                boldCells.push(`A${currentRow}`, `E${currentRow}`);
                currentRow++;

                sheetData.push(['']);
                currentRow++;
            });
        }

        // Add month total
        sheetData.push([
            t('estimation_for', { month: monthName }),
            '',
            '',
            '',
            tipsReconciliation?.data?.tipsReconciliationSummary?.monthTotal,
            ''
        ]);
        boldCells.push(`A${currentRow}`, `E${currentRow}`);

        XLSXUtils.sheet_add_aoa(sheet, sheetData);

        const workbook = XLSXUtils.book_new();
        const boldStyle = { font: { bold: true } };

        boldCells.forEach((cell) => {
            if (sheet[cell]) sheet[cell].s = boldStyle;
        });

        const wscols = [
            { wch: 20 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 30 },
            { wch: 20 }
        ];
        sheet['!cols'] = wscols;

        XLSXUtils.book_append_sheet(workbook, sheet, 'Report');

        XLSXWriteFile(
            workbook,
            `Tips Active Payroll ${formatDate(
                dayjs(),
                DATE_FORMAT.MONTH_YEAR
            )}.xlsx`
        );
    };

    const handleDownloadPdf = async () => {
        try {
            let response =
                await PaymentsAPIServiceV2.getInstance().getEstimatedTipsReconciliationReportPDF(
                    selectedMonth,
                    selectedYear
                );

            const anchor = document.createElement('a');
            anchor.href = response?.data?.fileUrl;
            anchor.click();
            anchor.remove();
        } catch (error) {
            console.error('Error downloading active payroll:', error);
        }
    };

    // Create dropdown choices from periods
    const periodChoices = periods?.map((period) => ({
        value: period.label,
        label: `${period.label} (${formatDate(
            period.start,
            DATE_FORMAT.DAY_MONTH,
            true
        )} - ${formatDate(period.end, DATE_FORMAT.DAY_MONTH, true)})`
    }));

    return (
        <div>
            <MDRegular sx={{ mb: 3, color: '#667085' }}>
                {t('tips.active_payroll_description')}
            </MDRegular>

            {periods?.length > 1 && (
                <>
                    <SearchGrid container justifyContent={'space-between'}>
                        <Stack
                            direction={'row'}
                            alignItems={'center'}
                            gap={1}
                            justifyContent={'space-between'}
                        >
                            <div>
                                <SMBold noWrap={true}>
                                    {t('select_period')}
                                </SMBold>
                            </div>
                            <Dropdown
                                style={{ width: '280px' }}
                                choices={periodChoices}
                                value={selectedPeriod?.label || ''}
                                onChange={(value) => {
                                    const selected = periods?.find(
                                        (p) => p.label === value
                                    );
                                    if (selected) {
                                        setSelectedPeriod(selected);
                                    }
                                }}
                            />
                        </Stack>
                    </SearchGrid>

                    <Box m={4}></Box>
                </>
            )}

            <TableCard>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    mr={2}
                >
                    <CardHeader
                        label={t('estimation_for', { month: dateHeader })}
                    ></CardHeader>
                    <span style={{ display: 'flex', gap: '8px' }}>
                        <SecondaryButtonRegular
                            onClick={onPressDownload}
                            size="small"
                            sx={{ borderRadius: '5px' }}
                        >
                            {t('download_as_excel')}
                        </SecondaryButtonRegular>
                        <PrimaryButton onClick={handleDownloadPdf} size="small">
                            {t('download_as_pdf')}
                        </PrimaryButton>
                    </span>
                </Stack>
                <Divider style={{ width: '100%' }} />
                <TableContainer>
                    <Table>
                        <TableBody>
                            {/* Hourly employees - multiple periods */}
                            {tipsReconciliation?.data?.tipsReconciliationSummary?.hourlyEmployeesTipsSummary?.map(
                                (period, index) => (
                                    <TableRow key={`hourly-${index}`}>
                                        <TableCell width={'390px'}>
                                            {formatPayrollPeriod(
                                                period.payrollPeriodStart,
                                                period.payrollPeriodEnd
                                            )}
                                        </TableCell>
                                        <TableCell align="left">
                                            {t(
                                                'estimated_hourly_employee_tips'
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {getBalanceLabel(
                                                period.total,
                                                company.currency
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            )}

                            {/* Fulltime employees - multiple periods */}
                            {tipsReconciliation?.data?.tipsReconciliationSummary?.fullTimeEmployeesTipsSummary?.map(
                                (period, index) => (
                                    <TableRow key={`fulltime-${index}`}>
                                        <TableCell>
                                            {formatPayrollPeriod(
                                                period.payrollPeriodStart,
                                                period.payrollPeriodEnd
                                            )}
                                        </TableCell>
                                        <TableCell align="left">
                                            {t(
                                                'estimated_fulltime_employee_tips'
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {getBalanceLabel(
                                                period.total,
                                                company.currency
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            )}
                        </TableBody>

                        <TableFooter>
                            <TableRow>
                                <TableFooterCell>
                                    <SMBold>{t('total_for_january')}</SMBold>
                                </TableFooterCell>
                                <TableFooterCell></TableFooterCell>
                                <TableFooterCell align="right">
                                    <SMBold>
                                        {getBalanceLabel(
                                            tipsReconciliation?.data
                                                ?.tipsReconciliationSummary
                                                ?.monthTotal,
                                            company.currency
                                        )}
                                    </SMBold>
                                </TableFooterCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </TableCard>

            <Box m={4}></Box>

            {/* Hourly employees - multiple periods */}
            {tipsReconciliation?.data?.hourlyEmployeesTipsReconciliation?.map(
                (period, index) => (
                    <div key={`hourly-table-${index}`}>
                        <TipsReconciliationTable
                            items={period.tipsTransactions || []}
                            label={t('hourly_employees')}
                            chipText={formatPayrollPeriod(
                                period.payrollPeriodStart,
                                period.payrollPeriodEnd
                            )}
                            totalAmount={getBalanceLabel(
                                period.total,
                                company.currency
                            )}
                        />
                        <Box m={4}></Box>
                    </div>
                )
            )}

            {/* Fulltime employees - multiple periods */}
            {tipsReconciliation?.data?.fullTimeEmployeesTipsReconciliation?.map(
                (period, index) => {
                    const fullTimeArray =
                        tipsReconciliation?.data
                            ?.fullTimeEmployeesTipsReconciliation;
                    const isLastItem = index === fullTimeArray.length - 1;

                    return (
                        <div key={`fulltime-table-${index}`}>
                            <TipsReconciliationTable
                                items={period.tipsTransactions || []}
                                totalAmount={getBalanceLabel(
                                    period.total,
                                    company.currency
                                )}
                                chipText={formatPayrollPeriod(
                                    period.payrollPeriodStart,
                                    period.payrollPeriodEnd
                                )}
                                label={t('fulltime_employees')}
                            />
                            {!isLastItem && <Box m={4}></Box>}
                        </div>
                    );
                }
            )}
        </div>
    );
};
